<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
    <div class="max-w-md w-full">
      <div class="bg-white rounded-lg shadow-lg p-8">
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-gray-800">Set New Password</h1>
          <p class="text-gray-600 mt-2">Enter your new password below</p>
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <div class="flex items-center">
            <i class="fas fa-check-circle mr-2"></i>
            <span>{{ successMessage }}</span>
          </div>
          <div class="mt-3">
            <router-link
              to="/login"
              class="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <i class="fas fa-sign-in-alt mr-2"></i>
              Go to Login
            </router-link>
          </div>
        </div>

        <!-- Invalid Token Message -->
        <div v-if="invalidToken" class="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div class="flex items-center">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            <span>This password reset link is invalid or has expired.</span>
          </div>
          <div class="mt-3">
            <router-link
              to="/forgot-password"
              class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <i class="fas fa-envelope mr-2"></i>
              Request New Link
            </router-link>
          </div>
        </div>

        <!-- Reset Password Form -->
        <form v-if="!successMessage && !invalidToken" @submit.prevent="handleResetPassword" class="space-y-6">
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <input
              id="password"
              type="password"
              v-model="password"
              required
              minlength="6"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your new password (min 6 characters)"
            >
          </div>

          <div>
            <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              v-model="confirmPassword"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Confirm your new password"
            >
          </div>

          <!-- Password Strength Indicator -->
          <div v-if="password" class="space-y-2">
            <div class="text-sm font-medium text-gray-700">Password Strength:</div>
            <div class="flex space-x-1">
              <div class="flex-1 h-2 rounded-full" :class="passwordStrengthColor"></div>
            </div>
            <div class="text-xs text-gray-600">
              {{ passwordStrengthText }}
            </div>
          </div>

          <!-- Password Match Indicator -->
          <div v-if="confirmPassword && password !== confirmPassword" class="text-red-600 text-sm">
            <i class="fas fa-times-circle mr-1"></i>
            Passwords do not match
          </div>

          <button
            type="submit"
            :disabled="loading || !isFormValid"
            class="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="loading" class="flex items-center justify-center">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              Resetting Password...
            </span>
            <span v-else class="flex items-center justify-center">
              <i class="fas fa-lock mr-2"></i>
              Reset Password
            </span>
          </button>
        </form>

        <!-- Error Message -->
        <div v-if="error" class="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div class="flex items-center">
            <i class="fas fa-exclamation-circle mr-2"></i>
            <span>{{ error }}</span>
          </div>
        </div>

        <!-- Back to Login -->
        <div v-if="!successMessage" class="mt-6 text-center">
          <router-link
            to="/login"
            class="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-center"
          >
            <i class="fas fa-arrow-left mr-2"></i>
            Back to Login
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { apiService } from '../services/apiService'
import type { ResetPasswordRequest, ApiError } from '../types/api'

const router = useRouter()
const route = useRoute()

const password = ref<string>('')
const confirmPassword = ref<string>('')
const loading = ref<boolean>(false)
const error = ref<string | null>(null)
const successMessage = ref<string | null>(null)
const invalidToken = ref<boolean>(false)
const token = ref<string>('')

const isFormValid = computed(() => {
  return password.value.length >= 6 && 
         confirmPassword.value === password.value &&
         token.value
})

const passwordStrength = computed(() => {
  const pwd = password.value
  let strength = 0
  
  if (pwd.length >= 6) strength++
  if (pwd.length >= 8) strength++
  if (/[A-Z]/.test(pwd)) strength++
  if (/[a-z]/.test(pwd)) strength++
  if (/[0-9]/.test(pwd)) strength++
  if (/[^A-Za-z0-9]/.test(pwd)) strength++
  
  return strength
})

const passwordStrengthColor = computed(() => {
  const strength = passwordStrength.value
  if (strength < 2) return 'bg-red-400'
  if (strength < 4) return 'bg-yellow-400'
  if (strength < 6) return 'bg-blue-400'
  return 'bg-green-400'
})

const passwordStrengthText = computed(() => {
  const strength = passwordStrength.value
  if (strength < 2) return 'Weak'
  if (strength < 4) return 'Fair'
  if (strength < 6) return 'Good'
  return 'Strong'
})

const handleResetPassword = async (): Promise<void> => {
  try {
    loading.value = true
    error.value = null

    if (password.value !== confirmPassword.value) {
      error.value = 'Passwords do not match'
      return
    }

    const requestData: ResetPasswordRequest = {
      token: token.value,
      password: password.value
    }

    const response = await apiService.resetPassword(requestData)
    
    successMessage.value = response.message
    console.log('Password reset successful')
  } catch (err) {
    const apiError = err as ApiError
    error.value = apiError.error || 'Failed to reset password. Please try again.'
    
    // Check if it's a token error
    if (apiError.error?.includes('Invalid or expired')) {
      invalidToken.value = true
    }
    
    console.error('Reset password error:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  // Get token from query params
  const queryToken = route.query.token as string
  
  if (!queryToken) {
    console.error('No reset token provided')
    invalidToken.value = true
    return
  }
  
  token.value = queryToken
  console.log('Reset token found:', queryToken)
})
</script>

<style scoped>
/* Additional styles if needed */
</style> 